import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { clearPortalLocalStorage } from '@rsos/base-utils/clearPortalLocalStorage';
import { getSessionAge } from '@rsos/base-utils/metaTags';
import { FullLogo } from '@rsos/components/capstone/Layout/Navigation/common/logo/RapidSOSLogo';
import { Box, LinkComponent, Text } from '@rsos/components/capstone/base';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import {
  type FetchUserResponse,
  fetchUser,
  getTokens,
  logout,
} from '@rsos/sinatra';
import {
  DECRYPT_IRP_TOKEN_SUCCESS,
  PROVIDER_LOGIN_SUCCESS,
  SESSION_START,
  SET_IRP_VERSION,
} from '@rsos/sinatra/src/actions/types';
import { captureException, configureUserScope } from '@rsos/utils/sentry';
import { notifyError } from '@rsos/utils/toastUtils';
import { trackSSOLogin } from '../../actions/authTrackings';
import { persistor } from '../../store';
import { AppRedirecting } from '../Landing/Landing.styles';
import {
  EMAIL_ERROR_MESSAGE,
  EMAIL_VALIDATION_REGEX,
  LOGIN_BUTTON_TEXT,
  PORTAL_EULA_LINK,
  PRIVACY_POLICY_LINK,
} from '../SSOLogin/SSOLogin';
import {
  EmailField,
  EmailLabel,
  LoginBox,
  LoginButton,
  LoginWrapper,
  StyledForm,
  StyledPage,
} from '../SSOLogin/SSOLogin.styles';
import authenticateWithSSO from '../SSOLogin/authenticateWithSSO';
import authorizeWithSSO from '../SSOLogin/authorizeWithSSO';
import { claimSsoAuthorizationCode } from '../SSOLogin/claimSsoAuthorizationCode';
import determineAppPath from '../SSOLogin/determineAppPath';
import parseQueryString from '../SSOLogin/parseQueryString';

export const IRP_SSO_PATH = '/irp-sso';
export const IRP_SSO_LOGIN_ERROR_MESSAGE =
  'Login failed: We could not authenticate you using SSO for iRP. If this error persists, please reach out to your system admin.';
export const IRP_SSO_ACCESS_ERROR_MESSAGE =
  'Login failed: This SSO account is not authorized to launch iRP. Please use a Portal account with iRP access or contact your system admin.';
export const IRP_SSO_QUERY_ERROR_MESSAGE =
  'Login failed: The iRP launch request is missing a valid query.';

class IRPSSOLoginAPIError extends Error {
  userMessage: string | null;

  constructor(message: string, userMessage: string | null = null) {
    super(message);
    this.name = 'IRPSSOLoginAPIError';
    this.userMessage = userMessage;
  }
}

type QueryParam = string | (string | null)[] | null | undefined;
type QueryParams = Record<string, QueryParam>;
type ErrorExtras = Record<string, unknown>;
type AuthorizationFailureOptions = {
  clearAuth?: boolean;
};
type IRPSSOState = {
  sinatra?: {
    auth?: {
      tokens?: {
        token?: string | null;
      };
    };
    user?: {
      isLoggedIn?: boolean;
    };
  };
};
type IRPSSODispatch = ThunkDispatch<unknown, unknown, AnyAction>;
type IRPUser = FetchUserResponse & {
  currentRole?: {
    application?: string;
  };
  email?: string;
  profile?: FetchUserResponse['profile'] & {
    email?: string;
  };
};
type SessionStartAction = {
  type: typeof SESSION_START;
  sessionStart: number;
  sessionExpiration: number;
};
type SetIRPVersionAction = {
  type: typeof SET_IRP_VERSION;
  version: string;
};
type IRPLaunchContextAction = {
  type: typeof DECRYPT_IRP_TOKEN_SUCCESS;
  data: {
    rsp_session_token: string;
    query: string;
    isICSP: boolean;
  };
};

const validationSchema = Yup.object({
  email: Yup.string()
    .matches(EMAIL_VALIDATION_REGEX, EMAIL_ERROR_MESSAGE)
    .required(EMAIL_ERROR_MESSAGE),
});

const getSingleQueryParam = (value: QueryParam): string | null | undefined => {
  if (Array.isArray(value)) {
    return value[0];
  }
  return value;
};

const hasMultipleQueryValues = (value: QueryParam): boolean =>
  Array.isArray(value);

const createSessionStartAction = (): SessionStartAction => {
  const now = Math.floor(Date.now() / 1000);
  return {
    type: SESSION_START,
    sessionStart: now,
    sessionExpiration: now + getSessionAge(),
  };
};

const createSetIRPVersionAction = (version: string): SetIRPVersionAction => ({
  type: SET_IRP_VERSION,
  version,
});

const createIRPLaunchContextAction = (
  token: string,
  query: string,
): IRPLaunchContextAction => ({
  // Match the legacy irp_token decrypt result exactly enough for the
  // emergency_data app to bootstrap from irp_access_token and queryURI.
  // This preserves backwards compatibility for all existing iRP UI code
  // without adding a new shared Sinatra API just for this route.
  type: DECRYPT_IRP_TOKEN_SUCCESS,
  data: {
    rsp_session_token: token,
    query,
    isICSP: false,
  },
});

export const isValidIRPQuery = (query: unknown): query is string => {
  // CAD launch payloads intentionally stay narrow: iRP v2 only supports
  // phone-number and alert lookup URIs. Rejecting arbitrary URLs here keeps
  // this browser-entry route from becoming a generic authenticated redirector.
  //
  // The payload character set mirrors deeplink-service RequestSchemaQuery.
  // Keep schemes lowercase because existing iRP consumers parse queryURI with
  // lowercase includes/split checks such as tel:// and alert://. Do not accept
  // lookalike schemes like alert-id:// unless downstream parsers change too.
  return typeof query === 'string' && /^(tel|alert):\/\/[\w\-+@]+$/.test(query);
};

export const createIRPSSORedirectURI = (query: string): string => {
  // WorkOS/Scorpius will return the browser to this route after IdP login.
  // Preserve the original CAD query across that external redirect so the CAD
  // does not need to store or replay RapidSOS credentials.
  const { protocol, host } = window.location;
  const redirectURL = new URL(`${protocol}//${host}${IRP_SSO_PATH}`);
  redirectURL.searchParams.set('query', query);
  return redirectURL.toString();
};

const getStoredAccessToken = (
  storedAccessToken?: string | null,
): string | null => {
  // Already-authenticated users should be able to open another iRP launch
  // without resetting their fixed portal session clock. Prefer Redux, but fall
  // back to Sinatra token storage for reloads where Redux has not hydrated yet.
  if (storedAccessToken) {
    return storedAccessToken;
  }

  try {
    return getTokens()?.token || null;
  } catch {
    return null;
  }
};

const reportAPIError = (
  errorInstance: Error,
  errorExtras: ErrorExtras = {},
): void => {
  captureException(errorInstance, errorExtras);
};

const IRPSSOLogin = () => {
  const dispatch = useDispatch<IRPSSODispatch>();
  const isAuthorizing = useRef(false);
  const isLaunching = useRef(false);
  const shouldClearAuthOnFailure = useRef(false);
  const storedAccessToken = useSelector(
    (state: IRPSSOState) => state.sinatra?.auth?.tokens?.token,
  );
  const isLoggedIn = useSelector(
    (state: IRPSSOState) => state.sinatra?.user?.isLoggedIn,
  );
  const [isAuthenticationSuccess, setIsAuthenticationSuccess] = useState(false);
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const parsedArgs = useMemo(
    () => parseQueryString(window.location.search) as QueryParams,
    [],
  );
  // Business rule: every launch or callback value that affects authentication
  // must be unambiguous. parseQueryString expands comma-separated values, but
  // repeated raw query params can collapse to the last value, so inspect the
  // original URL before trusting parsedArgs.
  const rawSearchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );
  const launchQuery = getSingleQueryParam(parsedArgs.query);
  const initialEmail = getSingleQueryParam(parsedArgs.email) || '';
  const code = getSingleQueryParam(parsedArgs.code);
  const callbackState = getSingleQueryParam(parsedArgs.state);
  const callbackError = getSingleQueryParam(parsedArgs.error);
  const hasCallbackCode = rawSearchParams.has('code');
  const hasCallbackError = rawSearchParams.has('error');
  const hasRepeatedLaunchQuery = rawSearchParams.getAll('query').length > 1;
  const hasMultipleLaunchQuery = hasMultipleQueryValues(parsedArgs.query);
  const hasAmbiguousLaunchQuery =
    hasRepeatedLaunchQuery || hasMultipleLaunchQuery;
  const hasAmbiguousSSOCallback =
    [parsedArgs.code, parsedArgs.state, parsedArgs.error].some(
      hasMultipleQueryValues,
    ) ||
    ['code', 'state', 'error'].some(
      queryParam => rawSearchParams.getAll(queryParam).length > 1,
    );

  const failAuthorization = useCallback(
    (
      error: unknown,
      extras: ErrorExtras = {},
      { clearAuth = false }: AuthorizationFailureOptions = {},
    ) => {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error));
      const userMessage =
        errorInstance instanceof IRPSSOLoginAPIError
          ? errorInstance.userMessage
          : null;
      // Preserve any earlier decision to clear auth. Some failures happen
      // after Scorpius has returned tokens; those partial credentials must be
      // removed even if a later cleanup path calls this without clearAuth.
      shouldClearAuthOnFailure.current =
        shouldClearAuthOnFailure.current || clearAuth;
      reportAPIError(errorInstance, extras);
      setFailureMessage(userMessage || IRP_SSO_LOGIN_ERROR_MESSAGE);
      setIsAuthenticationSuccess(false);
    },
    [],
  );

  const redirectToIRP = useCallback(
    async ({
      token,
      shouldStartSession = false,
    }: {
      token: string | null;
      shouldStartSession?: boolean;
    }) => {
      if (isLaunching.current) {
        return;
      }

      try {
        isLaunching.current = true;
        setIsRedirecting(true);

        if (!token) {
          throw new IRPSSOLoginAPIError('Missing access token for iRP launch');
        }

        if (!isValidIRPQuery(launchQuery)) {
          throw new IRPSSOLoginAPIError('Missing or invalid iRP query');
        }

        // Business rule: do not start a session or write iRP launch context
        // until Scorpius tokens have been proven usable for a Portal user.
        // This keeps unsupported SSO users from leaving partial iRP state that
        // downstream apps would treat as a valid launch.
        const user = (await dispatch(fetchUser())) as IRPUser;
        const applicationPath = determineAppPath(user);
        if (applicationPath !== '/home') {
          // iRP is a RapidSOS Portal/Capstone experience. Users authenticated
          // only for other apps must not get partial iRP state or a new session.
          throw new IRPSSOLoginAPIError(
            'SSO user cannot launch iRP',
            IRP_SSO_ACCESS_ERROR_MESSAGE,
          );
        }

        if (shouldStartSession) {
          dispatch(createSessionStartAction());
        }

        dispatch(createSetIRPVersionAction('2'));
        dispatch(createIRPLaunchContextAction(token, launchQuery));

        try {
          configureUserScope({
            email: user?.profile?.email || user?.email,
            version: process.env.REACT_APP_FE_VERSION || '',
          });
        } catch (error) {
          captureException(error);
        }

        try {
          trackSSOLogin(user?.currentRole?.application);
        } catch (error) {
          captureException(error);
        }

        window.location.assign('/home');
      } catch (error) {
        failAuthorization(
          error,
          { hasQuery: Boolean(launchQuery) },
          { clearAuth: shouldStartSession },
        );
      } finally {
        isLaunching.current = false;
        setIsRedirecting(false);
      }
    },
    [dispatch, failAuthorization, launchQuery],
  );

  useEffect(() => {
    if (hasAmbiguousLaunchQuery || !isValidIRPQuery(launchQuery)) {
      // Fail before SSO starts. Without a valid query this route cannot safely
      // know what CAD event to show after authentication. Repeated query params
      // are rejected for the same reason: there must be only one launch target.
      const error = new IRPSSOLoginAPIError('Missing or invalid iRP query');
      shouldClearAuthOnFailure.current = !isLoggedIn;
      reportAPIError(error, { query: launchQuery });
      setFailureMessage(IRP_SSO_QUERY_ERROR_MESSAGE);
      return;
    }

    if (hasAmbiguousSSOCallback) {
      // A WorkOS callback should contain only one code/state/error value. If a
      // malformed URL provides multiple values, fail closed instead of choosing
      // one and risking a confusing or mismatched token exchange.
      const error = new IRPSSOLoginAPIError('Ambiguous SSO callback');
      shouldClearAuthOnFailure.current = !isLoggedIn;
      reportAPIError(error, {
        hasCode: Boolean(parsedArgs.code),
        hasState: Boolean(parsedArgs.state),
        hasError: Boolean(parsedArgs.error),
      });
      setFailureMessage(IRP_SSO_LOGIN_ERROR_MESSAGE);
      return;
    }

    if (hasCallbackCode && !code) {
      // A callback with an empty code is malformed. Treat it as a terminal auth
      // failure instead of falling back to the email form and creating a second
      // SSO attempt for a URL that already represents a failed callback.
      shouldClearAuthOnFailure.current = !isLoggedIn;
      reportAPIError(new IRPSSOLoginAPIError('Malformed SSO callback'), {
        hasCode: true,
        hasState: Boolean(parsedArgs.state),
        hasError: Boolean(parsedArgs.error),
      });
      setFailureMessage(IRP_SSO_LOGIN_ERROR_MESSAGE);
      return;
    }

    if (hasCallbackError) {
      // A provider/user error is terminal even when the URL also includes a
      // code. Prefer the explicit error over attempting a token exchange with
      // a potentially stale or mismatched one-time code.
      shouldClearAuthOnFailure.current = !isLoggedIn;
      reportAPIError(new IRPSSOLoginAPIError('User error'), {
        error: callbackError || 'unknown',
      });
      setFailureMessage(IRP_SSO_LOGIN_ERROR_MESSAGE);
      return;
    }

    if (isLoggedIn) {
      // Backwards-compatible session behavior: reuse the existing Portal
      // session and only change iRP launch context for the new query.
      redirectToIRP({
        token: getStoredAccessToken(storedAccessToken),
        shouldStartSession: false,
      });
      return;
    }

    if (code) {
      setIsAuthenticationSuccess(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const resetCallbackURL = () => {
      const resetURL = new URL(
        window.location.pathname,
        window.location.origin,
      );
      if (!hasAmbiguousLaunchQuery && isValidIRPQuery(launchQuery)) {
        resetURL.searchParams.set('query', launchQuery);
      }
      const resetPath = `${resetURL.pathname}${resetURL.search}`;
      // Strip one-time SSO callback fields while preserving a valid CAD query
      // so the user can retry without CAD having to open a new browser window.
      window.history.pushState({ path: resetPath }, '', resetPath);
    };

    const resetLoginAttempt = async () => {
      let cleanupFailed = false;
      const reportResetError = (error: unknown, phase: string) => {
        cleanupFailed = true;
        reportAPIError(
          error instanceof Error ? error : new Error(String(error)),
          { phase },
        );
      };

      if (shouldClearAuthOnFailure.current) {
        // Only clear auth state when this route may have persisted a partial
        // SSO callback token. A logged-in dispatcher who opens a bad iRP link
        // should see a launch error, not lose their existing Portal session.
        try {
          await dispatch(logout());
        } catch (error) {
          reportResetError(error, 'logout');
        }

        try {
          await persistor.flush();
        } catch (error) {
          reportResetError(error, 'persistorFlush');
        }

        try {
          clearPortalLocalStorage();
        } catch (error) {
          reportResetError(error, 'clearPortalLocalStorage');
        }

        try {
          if (window.indexedDB) {
            window.indexedDB.deleteDatabase('localforage');
          }
        } catch (error) {
          reportResetError(error, 'deleteIndexedDB');
        }

        if (!cleanupFailed) {
          shouldClearAuthOnFailure.current = false;
        }
      }

      resetCallbackURL();
    };

    if (failureMessage) {
      notifyError(failureMessage, {
        autoClose: false,
      });
      void resetLoginAttempt().catch(error => {
        reportAPIError(
          error instanceof Error ? error : new Error(String(error)),
          { phase: 'resetCallbackURL' },
        );
      });
    }
  }, [dispatch, hasAmbiguousLaunchQuery, launchQuery, failureMessage]);

  useEffect(() => {
    const doAuthorization = async () => {
      if (isAuthorizing.current) {
        return;
      }

      if (!code) {
        return;
      }

      // Claim before any await so a remount cannot start a second exchange of
      // the same single use workos code.
      if (!claimSsoAuthorizationCode(code)) {
        return;
      }

      try {
        isAuthorizing.current = true;
        const tokens = await authorizeWithSSO(code, callbackState || undefined);
        if (!tokens?.token) {
          throw new IRPSSOLoginAPIError(
            'Token exchange returned no access token',
          );
        }

        // authorizeWithSSO persists tokens for existing SSO behavior; dispatch
        // PROVIDER_LOGIN_SUCCESS as well so the Redux state is immediately in
        // the same shape as the legacy /sso callback path.
        dispatch({
          type: PROVIDER_LOGIN_SUCCESS,
          tokens,
        });
        // From this point forward, a failure means a partial SSO callback state
        // may exist in local storage or redux-persist and must be cleaned up.
        shouldClearAuthOnFailure.current = true;
        await redirectToIRP({ token: tokens.token, shouldStartSession: true });
      } catch (error) {
        failAuthorization(
          error,
          {
            code,
            hasState: Boolean(callbackState),
            hasQuery: Boolean(launchQuery),
          },
          { clearAuth: true },
        );
      } finally {
        isAuthorizing.current = false;
      }
    };

    if (
      isAuthenticationSuccess &&
      code &&
      !hasAmbiguousLaunchQuery &&
      !hasAmbiguousSSOCallback &&
      isValidIRPQuery(launchQuery)
    ) {
      doAuthorization();
    }
  }, [
    callbackState,
    code,
    dispatch,
    failAuthorization,
    hasAmbiguousLaunchQuery,
    hasAmbiguousSSOCallback,
    isAuthenticationSuccess,
    launchQuery,
    redirectToIRP,
  ]);

  return (
    <StyledPage>
      {isRedirecting ? (
        <AppRedirecting />
      ) : (
        <LoginWrapper>
          <LoginBox>
            <FullLogo height={50} />
            <Formik
              initialValues={{ email: initialEmail }}
              validationSchema={validationSchema}
              onSubmit={async (values, { setSubmitting }) => {
                try {
                  if (
                    hasAmbiguousLaunchQuery ||
                    !isValidIRPQuery(launchQuery)
                  ) {
                    throw new IRPSSOLoginAPIError(
                      'Missing or invalid iRP query',
                    );
                  }

                  // This step only asks Scorpius/WorkOS where to send the
                  // browser. No RapidSOS token is available until the callback
                  // code is exchanged and the returned user is verified.
                  setIsAuthenticationSuccess(true);
                  const authorizationURL = await authenticateWithSSO(
                    values.email,
                    createIRPSSORedirectURI(launchQuery),
                  );

                  if (!authorizationURL) {
                    throw new IRPSSOLoginAPIError('No authorization URL found');
                  }

                  window.location.assign(authorizationURL);
                } catch (error) {
                  shouldClearAuthOnFailure.current = !isLoggedIn;
                  failAuthorization(error, { hasQuery: Boolean(launchQuery) });
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                isSubmitting,
                isValid,
                setFieldValue,
                touched,
                values,
              }) => (
                <StyledForm>
                  <Box width="100%">
                    <Box>
                      <EmailField>
                        <EmailLabel htmlFor="email">Email</EmailLabel>
                        <InputText
                          id="email"
                          name="email"
                          placeholder="email@domain.com"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          shouldErrorTakeSpace
                          error={
                            touched.email && typeof errors.email === 'string'
                              ? errors.email
                              : null
                          }
                        />
                      </EmailField>
                    </Box>
                    <Box mt={36}>
                      <LoginButton
                        isLoadingProp={isAuthenticationSuccess}
                        disabled={isSubmitting || !isValid || !values.email}
                        dataName="irp-sso-button"
                      >
                        {LOGIN_BUTTON_TEXT}
                      </LoginButton>
                    </Box>
                  </Box>
                  <Box mt={36}>
                    <Text size="small" textAlign="center">
                      By logging in, you agree to be bound by the <br />{' '}
                      <LinkComponent to={PORTAL_EULA_LINK} withBg={false}>
                        Unite EULA
                      </LinkComponent>{' '}
                      and{' '}
                      <LinkComponent to={PRIVACY_POLICY_LINK} withBg={false}>
                        Privacy Policy
                      </LinkComponent>
                    </Text>
                  </Box>
                </StyledForm>
              )}
            </Formik>
          </LoginBox>
        </LoginWrapper>
      )}
    </StyledPage>
  );
};

export default IRPSSOLogin;
